import type {
    OfficeActionHeaderResponseData,
    AgencyInfo as ApiAgencyInfo,
    CitedDocumentInfo,
    ConclusiveOpinionInfo,
    ExaminerConclusionInfo,
    BodySectionInfo,
} from '../apis/oar-task-api';
import type {
    OfficeActionHeader,
    AgencyInfo,
    CitedDocument,
    ConclusiveOpinion,
    ExaminerConclusion,
    OfficeActionBodySection,
    PatentOffice,
    IssueNature,
} from '../types/oar-types';

/**
 * 映射 AgencyInfo
 */
function mapAgencyInfo(apiAgency: ApiAgencyInfo): AgencyInfo {
    return {
        name: apiAgency.name,
        address: apiAgency.address,
        contactPerson: apiAgency.contact_person,
        phoneNumber: apiAgency.phone_number,
    };
}

/**
 * 映射 OfficeActionHeader
 *
 * 将 API 返回的 Header 数据转换为领域模型
 */
export function mapOfficeActionHeader(apiHeader: OfficeActionHeaderResponseData): OfficeActionHeader {
    return {
        documentType: apiHeader.document_type,
        authority: apiHeader.authority as PatentOffice,
        documentNumber: apiHeader.document_number,
        issueDate: apiHeader.issue_date,
        agency: apiHeader.agency ? mapAgencyInfo(apiHeader.agency) : undefined as any,
        applicationNumber: apiHeader.application_number,
        documentSequenceNumber: apiHeader.document_sequence_number,
        applicant: apiHeader.applicant,
        inventionTitle: apiHeader.invention_title,
        barcodes: apiHeader.barcodes,
    };
}

/**
 * 映射 CitedDocument
 *
 * 将 API 返回的引用文献数据转换为领域模型
 */
export function mapCitedDocument(apiDoc: CitedDocumentInfo): CitedDocument {
    return {
        number: apiDoc.number,
        documentNumberOrName: apiDoc.document_number_or_name,
        publicationDate: apiDoc.publication_date,
        isPatent: apiDoc.is_patent,
        documentText: apiDoc.document_text || '',
    };
}

/**
 * 映射 ConclusiveOpinion
 *
 * 将 API 返回的结论性意见数据转换为领域模型
 */
export function mapConclusiveOpinion(apiOpinion: ConclusiveOpinionInfo): ConclusiveOpinion {
    // 解析问题性质
    const issueNature = apiOpinion.issue_nature === '实质性问题' || apiOpinion.issue_nature.toLowerCase().includes('substantive')
        ? 'substantive' as IssueNature
        : 'formal' as IssueNature;

    return {
        patentOffice: apiOpinion.patent_office as PatentOffice,
        articleNumber: apiOpinion.article_number,
        articleTitle: apiOpinion.article_title,
        issueNature,
        isSelected: apiOpinion.is_selected,
        isClaimRelated: apiOpinion.is_claim_related,
        affectedClaims: apiOpinion.affected_claims,
        otherNotes: apiOpinion.other_notes || '',
        opinionId: apiOpinion.opinion_id,
    };
}

/**
 * 映射 ExaminerConclusion
 *
 * 将 API 返回的审查员结论数据转换为领域模型
 */
export function mapExaminerConclusion(apiConclusion: ExaminerConclusionInfo): ExaminerConclusion {
    return {
        patentOffice: apiConclusion.patent_office as PatentOffice,
        conclusionText: apiConclusion.conclusion_text,
    };
}

/**
 * 映射 BodySection
 *
 * 将 API 返回的正文段落数据转换为领域模型
 */
export function mapBodySection(apiSection: BodySectionInfo): OfficeActionBodySection {
    return {
        legalBasis: apiSection.legal_basis,
        affectedClaims: apiSection.affected_claims,
        claimText: apiSection.claim_text,
        citedDocumentNumbers: apiSection.cited_document_numbers,
        technicalComparison: apiSection.technical_comparison,
        conclusionStatement: apiSection.conclusion_statement,
        amendmentSuggestion: apiSection.amendment_suggestion || '',
        fullText: apiSection.full_text,
    };
}

/**
 * 批量映射 CitedDocument 列表
 */
export function mapCitedDocuments(apiDocs: CitedDocumentInfo[]): CitedDocument[] {
    return apiDocs.map(mapCitedDocument);
}

/**
 * 批量映射 ConclusiveOpinion 列表
 */
export function mapConclusiveOpinions(apiOpinions: ConclusiveOpinionInfo[]): ConclusiveOpinion[] {
    return apiOpinions.map(mapConclusiveOpinion);
}

/**
 * 批量映射 ExaminerConclusion 列表
 */
export function mapExaminerConclusions(apiConclusions: ExaminerConclusionInfo[]): ExaminerConclusion[] {
    return apiConclusions.map(mapExaminerConclusion);
}

/**
 * 批量映射 BodySection 列表
 */
export function mapBodySections(apiSections: BodySectionInfo[]): OfficeActionBodySection[] {
    return apiSections.map(mapBodySection);
}
